/**
 * @file        src/components/promo/PromoGate.tsx
 * @description The single client component that owns the promo's open state, fires the analytics
 *              goals, and renders the button and the window together. All display policy lives
 *              here and nowhere else — that is what lets the next offer reuse PromoButton and
 *              PromoModal with different rules, and what lets these rules be read in one place.
 * @universal   knowledge/universals/components.md#promogate
 * @usage <PromoGate />
 *
 * @rule        BUTTON ONLY (owner decision 2026-10-20, ADR N-PROMO-PERMANENT-1). The window opens
 *              when the visitor presses the button and in no other way: no automatic show, no exit
 *              intent, no cooldown. Do not add a trigger back without a new owner decision.
 */

"use client";

import { useEffect, useRef, useState } from "react";
import { usePathname } from "next/navigation";

import PromoButton from "@/components/promo/PromoButton";
import PromoModal from "@/components/promo/PromoModal";
import { isPromoActive } from "@/lib/promo/promoConfig";
import { trackGoal } from "@/lib/metrika";

// ─── POLICY DATA ───

/**
 * Paths the promo never appears on. Data, not logic, so a future path is one line here.
 * A pathname matches when it equals an entry or starts with the entry plus a slash.
 */
const EXCLUDED_PATH_PREFIXES: readonly string[] = [
  "/admin",
  "/privacy",
  "/promo",
  "/terms",
  "/auth-return",
];

/** @param pathname current route @returns true when the promo must not appear here */
function isExcludedPath(pathname: string): boolean {
  return EXCLUDED_PATH_PREFIXES.some(
    (p) => pathname === p || pathname.startsWith(`${p}/`),
  );
}

// ─── COMPONENT ───

/**
 * Owns the promo's open state and its display policy.
 *
 * @input   none — the on/off state comes from promoConfig
 * @output  the button and the window, or null when the program is off or the path is excluded
 * @important isPromoActive() is called during render on purpose: it is a pure function of a
 *            constant start date in the past, so server and client agree and nothing hydrates
 *            differently. Turning the offer off is a code change, and every deploy rebuilds pages.
 */
export default function PromoGate() {
  const pathname = usePathname();
  const [open, setOpen] = useState(false);

  // Refs, not state: none of these should cause a render on their own.
  const buttonViewFired = useRef(false);
  const closeFired = useRef(false);

  const programOff = !isPromoActive();
  const hidden = programOff || isExcludedPath(pathname);

  // ── Button view goal, once per visit ──
  useEffect(() => {
    if (hidden || buttonViewFired.current) return;
    buttonViewFired.current = true;
    trackGoal("promo_button_view");
  }, [hidden]);

  // ── Close on navigation ──
  // The window is an overlay, not a page. Without this it survives a client-side navigation, so
  // the links INSIDE it — «Условия акции» and the consent link to /privacy — looked dead: they
  // navigated correctly and the destination rendered BEHIND the still-open window.
  // @rule A NAVIGATION IS NOT A DISMISSAL. `setOpen(false)` is called directly and deliberately
  // NOT through the dialog's onOpenChange: that path fires `promo_close`, and a visitor who clicks
  // through to the terms has not rejected the offer. See ADR N-PROMO-NAV-CLOSE-1.
  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  if (hidden) return null;

  return (
    <>
      <PromoButton
        onClick={() => {
          // The click is the ONLY goal allowed to fire more than once per visit.
          trackGoal("promo_button_click");
          setOpen(true);
        }}
      />
      <PromoModal
        open={open}
        onOpenChange={(next) => {
          if (!next && open && !closeFired.current) {
            // Once per visit, like every goal except the click.
            closeFired.current = true;
            trackGoal("promo_close");
          }
          setOpen(next);
        }}
      />
    </>
  );
}
